import type { TournamentMatch } from "./api";

// Orden de rondas en una cola de programación (revisión 2026-10-03): el
// Programador de Canchas manda un partido a la vez y salta al siguiente
// horario con cualquier 409. Si la Final (o una semifinal) salía antes que
// sus partidos previos, tomaba las 9:00 y después ningún horario servía para
// sus semifinales (terminarían después de que empieza la Final). Los previos
// van primero, como en «Ordenar automático» del torneo.

type RoundOrderMatch = Pick<TournamentMatch, "tournament_id" | "division_id" | "group_id" | "round_label">;

const ELIMINATION_RANK: Record<string, number> = {
  Preliminar: 0,
  "Round of 256": 1,
  "Round of 128": 2,
  "Round of 64": 3,
  "Round of 32": 4,
  "Round of 16": 5,
  Quarterfinals: 6,
  Semifinals: 7,
};

/** Rango de la ronda dentro de su categoría (menor = antes). Espejo de
 * `_schedule_round_precedence` del servidor, con dos diferencias:
 * - la fase de grupos («Jornada N» con grupo) va antes que los playoffs de
 *   round robin («Jornada N» sin grupo, incluida su preliminar);
 * - la Final y el 3er Lugar al final: de ellos no sale nadie (en playoffs
 *   de round robin el 3er Lugar quedaba antes que sus semifinales). */
export function roundOrderRank(match: Pick<RoundOrderMatch, "group_id" | "round_label">): number {
  const label = (match.round_label ?? "").trim();
  if (label === "Final" || label === "3er Lugar") return 10_000;
  if (label in ELIMINATION_RANK) return ELIMINATION_RANK[label];
  const numbered = label.match(/^(?:jornada|semana|ronda)\s*#?\s*(\d+)/i);
  if (numbered) return (match.group_id != null ? 100 : 1_000) + Number(numbered[1]);
  return 99;
}

/** La misma cola con los partidos previos antes que los que los esperan,
 * dentro de cada torneo y categoría. Cada categoría conserva sus lugares en
 * la cola (el orden Random o Por torneo entre categorías no cambia); solo se
 * reacomodan sus partidos por ronda, y en la misma ronda sigue el orden
 * recibido. */
export function feedersFirstQueue<T extends RoundOrderMatch>(queue: T[]): T[] {
  const indexesByScope = new Map<string, number[]>();
  queue.forEach((match, index) => {
    const key = `${match.tournament_id}|${match.division_id ?? ""}`;
    const indexes = indexesByScope.get(key);
    if (indexes) indexes.push(index);
    else indexesByScope.set(key, [index]);
  });
  const result = [...queue];
  for (const indexes of indexesByScope.values()) {
    const ordered = [...indexes].sort(
      (a, b) => roundOrderRank(queue[a]) - roundOrderRank(queue[b]) || a - b,
    );
    indexes.forEach((slot, position) => {
      result[slot] = queue[ordered[position]];
    });
  }
  return result;
}
